/**
 * Browser audio plumbing for Flux Agent dictation and hands-free voice mode: base64 conversion,
 * MediaRecorder format selection, an RMS-based end-of-utterance detector, microphone capture with
 * byte/duration ceilings, and reply playback. The detector and conversions are pure so they can be
 * unit-tested without a browser; capture and playback functions touch `navigator.mediaDevices`,
 * `AudioContext`, `Audio`, and `speechSynthesis` and must only run after a user gesture.
 *
 * @module apps/web/src/lib/fluxAgentVoice
 */

/** Encode bytes as standard base64 without overflowing the call stack on large buffers. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Decode standard base64 into bytes. Throws on malformed input. */
export function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Read a Blob or File into base64. */
export async function blobToBase64(blob: Blob): Promise<string> {
  return bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
}

/**
 * Recorder formats in preference order: Opus-in-WebM is smallest and widely transcribable; Safari
 * without WebM recording gets MP4/AAC next, ahead of Ogg, which Safari cannot record.
 */
export const RECORDER_MIME_PREFERENCES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/mpeg',
];

/**
 * Pick the first supported recorder format. Returns `''` (let the browser choose) when the
 * support probe is unavailable or nothing listed is supported.
 */
export function pickRecorderMimeType(isTypeSupported?: (type: string) => boolean): string {
  if (!isTypeSupported) return '';
  return (
    RECORDER_MIME_PREFERENCES.find(type => {
      try {
        return isTypeSupported(type);
      } catch {
        return false;
      }
    }) ?? ''
  );
}

/** Strip codec parameters so the server sees a plain media type such as `audio/webm`. */
export function baseMimeType(mimeType: string, fallback = 'audio/webm'): string {
  return mimeType.split(';')[0].trim() || fallback;
}

/** Root-mean-square amplitude of time-domain samples in the range [-1, 1]. */
export function rootMeanSquare(samples: ArrayLike<number>): number {
  if (!samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
}

/** Tuning for {@link createSilenceDetector}. All durations are milliseconds. */
export interface SilenceDetectorOptions {
  /** RMS level treated as speech. Defaults to 0.02 (quiet room, laptop microphone). */
  threshold?: number;
  /** Silence after speech that ends the utterance. Defaults to 1500. */
  silenceMs?: number;
  /** Cumulative loud time required before silence can end the utterance. Defaults to 250. */
  minSpeechMs?: number;
  /** Hard ceiling on the utterance. Defaults to 30000. */
  maxMs?: number;
  /** Give up when no speech starts within this window. Defaults to 15000. */
  noSpeechMs?: number;
}

/** Detector verdict for the latest sample. */
export type SilenceVerdict = 'continue' | 'silence' | 'max' | 'no-speech';

/** Stateful end-of-utterance detector fed with RMS levels and monotonically increasing timestamps. */
export interface SilenceDetector {
  /**
   * Feed one RMS level (0-1) sampled at `nowMs`, milliseconds since capture started. Returns
   * `continue`, or the reason the utterance should end: `silence`, `max`, or `no-speech`.
   */
  update(level: number, nowMs: number): SilenceVerdict;
  /** True once enough speech has been heard for the recording to be worth transcribing. */
  readonly heardSpeech: boolean;
}

/**
 * Create an end-of-utterance detector. Speech accumulates while the level is at or above the
 * threshold; once at least `minSpeechMs` has been heard, `silenceMs` of continuous quiet ends the
 * utterance. Independently, the utterance ends at `maxMs`, or reports `no-speech` when nothing
 * loud enough arrives within `noSpeechMs`.
 */
export function createSilenceDetector(options: SilenceDetectorOptions = {}): SilenceDetector {
  const threshold = options.threshold ?? 0.02;
  const silenceMs = options.silenceMs ?? 1500;
  const minSpeechMs = options.minSpeechMs ?? 250;
  const maxMs = options.maxMs ?? 30_000;
  const noSpeechMs = options.noSpeechMs ?? 15_000;
  let startedAt: number | null = null;
  let previousAt: number | null = null;
  let speechMs = 0;
  let quietSince: number | null = null;

  return {
    get heardSpeech() {
      return speechMs >= minSpeechMs;
    },
    update(level, nowMs) {
      if (startedAt === null) startedAt = nowMs;
      const elapsedStep = previousAt === null ? 0 : Math.max(0, nowMs - previousAt);
      previousAt = nowMs;
      if (level >= threshold) {
        speechMs += elapsedStep;
        quietSince = null;
      } else if (quietSince === null) {
        quietSince = nowMs;
      }
      if (nowMs - startedAt >= maxMs) return 'max';
      if (speechMs >= minSpeechMs) {
        if (quietSince !== null && nowMs - quietSince >= silenceMs) return 'silence';
      } else if (nowMs - startedAt >= noSpeechMs) {
        return 'no-speech';
      }
      return 'continue';
    },
  };
}

/** Why a microphone capture could not start, in user-presentable form. */
export class MicrophoneError extends Error {
  /** `unsupported`, `denied`, or `failed` (device missing, busy, or other). */
  readonly reason: 'unsupported' | 'denied' | 'failed';

  constructor(reason: 'unsupported' | 'denied' | 'failed', message: string) {
    super(message);
    this.reason = reason;
  }
}

/** True when this browser exposes everything needed to record audio. */
export function canRecordAudio(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

/** Ask for the microphone, translating permission and support failures into {@link MicrophoneError}. */
export async function openMicrophone(): Promise<MediaStream> {
  if (!canRecordAudio()) {
    throw new MicrophoneError(
      'unsupported',
      'This browser cannot record audio. Try a current Chrome, Edge, Firefox, or Safari.'
    );
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (error) {
    const name = (error as DOMException)?.name;
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      throw new MicrophoneError(
        'denied',
        'Microphone access was blocked. Allow it in your browser’s site settings to talk to Flux Agent.'
      );
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      throw new MicrophoneError('failed', 'No microphone was found.');
    }
    throw new MicrophoneError('failed', 'The microphone could not be started.');
  }
}

/** Stop every track so the browser's recording indicator turns off. */
export function closeMicrophone(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach(track => track.stop());
}

/** A finished recording ready for `/speech/transcribe`. */
export interface Recording {
  /** All recorded slices concatenated, typed with the recorder's full media type. */
  blob: Blob;
  /** Base media type without codec parameters. */
  mimeType: string;
  /** False when voice-activity detection never heard speech. */
  heardSpeech: boolean;
}

/** Options for {@link startRecording}. */
export interface RecordingOptions {
  /** Stop before the encoded audio exceeds this many bytes (with a safety margin). */
  maxBytes: number;
  /** Hard stop after this many milliseconds. Defaults to 120000. */
  maxMs?: number;
  /** When set, stop automatically at the end of an utterance using {@link createSilenceDetector}. */
  silence?: SilenceDetectorOptions;
  /** Receives a smoothed 0–1 input level for meters, roughly every 60 ms. */
  onLevel?: (level: number) => void;
  /** Aborting stops the recording and resolves with whatever was captured. */
  signal?: AbortSignal;
}

/** A running capture; `stop()` is idempotent and `done` resolves with the final recording. */
export interface RecordingHandle {
  /** End capture, release the analyser `AudioContext`, and flush the recorder; safe to repeat. */
  stop(): void;
  /**
   * Resolves with whatever was captured once the recorder stops, including after an abort or
   * byte-budget stop; rejects with a `failed` {@link MicrophoneError} on a recorder error.
   */
  done: Promise<Recording>;
}

/**
 * Record from an open microphone stream. Data is collected in one-second slices so the byte
 * ceiling can stop the recorder before the upload limit is crossed. With `silence`, an
 * `AudioContext` analyser watches levels and stops once the speaker pauses.
 */
export function startRecording(stream: MediaStream, options: RecordingOptions): RecordingHandle {
  const preferred = pickRecorderMimeType(MediaRecorder.isTypeSupported?.bind(MediaRecorder));
  const recorder = preferred
    ? new MediaRecorder(stream, { mimeType: preferred })
    : new MediaRecorder(stream);
  const chunks: Blob[] = [];
  let bytes = 0;
  const byteBudget = Math.max(1, Math.floor(options.maxBytes * 0.9));
  const detector = options.silence
    ? createSilenceDetector({ maxMs: options.maxMs, ...options.silence })
    : null;
  let context: AudioContext | null = null;
  // eslint-disable-next-line prefer-const -- stop() can run before the interval starts; a const would still be in its temporal dead zone then.
  let timer: number | undefined;
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.clearInterval(timer);
    void context?.close().catch(() => undefined);
    if (recorder.state !== 'inactive') recorder.stop();
  };

  const done = new Promise<Recording>((resolve, reject) => {
    recorder.ondataavailable = event => {
      if (!event.data.size) return;
      chunks.push(event.data);
      bytes += event.data.size;
      if (bytes >= byteBudget) stop();
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || preferred || 'audio/webm';
      resolve({
        blob: new Blob(chunks, { type }),
        mimeType: baseMimeType(type),
        heardSpeech: detector ? detector.heardSpeech : true,
      });
    };
    recorder.onerror = () => {
      stop();
      reject(new MicrophoneError('failed', 'Recording failed.'));
    };
  });

  options.signal?.addEventListener('abort', stop, { once: true });
  recorder.start(1000);

  const AudioContextCtor: typeof AudioContext | undefined =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  let analyser: AnalyserNode | null = null;
  if (AudioContextCtor && (detector || options.onLevel)) {
    try {
      context = new AudioContextCtor();
      analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
    } catch {
      analyser = null;
    }
  }
  const samples = new Float32Array(analyser?.fftSize ?? 1024);
  const startedAt = performance.now();
  const maxMs = options.maxMs ?? 120_000;
  let smoothed = 0;
  timer = window.setInterval(() => {
    const now = performance.now();
    if (now - startedAt >= maxMs) return stop();
    if (!analyser) return;
    analyser.getFloatTimeDomainData(samples);
    const level = rootMeanSquare(samples);
    smoothed = smoothed * 0.6 + Math.min(1, level * 8) * 0.4;
    options.onLevel?.(smoothed);
    if (detector && detector.update(level, now - startedAt) !== 'continue') stop();
  }, 60);

  return { stop, done };
}

/** A short silent 16-bit mono WAV, used to unlock media playback during a user gesture. */
export function silentWav(durationMs = 50, sampleRate = 8000): Uint8Array {
  const samples = Math.max(1, Math.round((sampleRate * durationMs) / 1000));
  const bytes = new Uint8Array(44 + samples * 2);
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, text: string) =>
    [...text].forEach((char, i) => view.setUint8(offset + i, char.charCodeAt(0)));
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, samples * 2, true);
  return bytes;
}

/**
 * Prime an audio element (and the speech synthesizer) from inside a click handler so later,
 * asynchronous replies may play on browsers with strict autoplay rules such as iOS Safari.
 */
export function unlockAudioPlayback(audio: HTMLAudioElement): void {
  const url = URL.createObjectURL(new Blob([silentWav() as BlobPart], { type: 'audio/wav' }));
  audio.src = url;
  audio
    .play()
    .catch(() => undefined)
    .finally(() => URL.revokeObjectURL(url));
  if (canSpeakWithBrowser()) window.speechSynthesis.speak(new SpeechSynthesisUtterance(''));
}

/**
 * Play base64 audio and resolve when playback ends; aborting pauses and resolves immediately.
 * Pass a previously unlocked element to reuse it, otherwise a new `Audio` is created.
 */
export function playBase64Audio(
  base64: string,
  mimeType: string,
  signal?: AbortSignal,
  audio: HTMLAudioElement = new Audio()
): Promise<void> {
  if (signal?.aborted) return Promise.resolve();
  const url = URL.createObjectURL(
    new Blob([base64ToBytes(base64) as BlobPart], { type: mimeType || 'audio/mpeg' })
  );
  audio.src = url;
  return new Promise<void>((resolve, reject) => {
    const finish = (error?: unknown) => {
      audio.pause();
      audio.onended = null;
      audio.onerror = null;
      URL.revokeObjectURL(url);
      signal?.removeEventListener('abort', onAbort);
      if (error) reject(error);
      else resolve();
    };
    const onAbort = () => finish();
    signal?.addEventListener('abort', onAbort, { once: true });
    audio.onended = () => finish();
    audio.onerror = () => finish(new Error('Audio playback failed.'));
    audio.play().catch(finish);
  });
}

/** True when the browser has a built-in speech synthesizer to fall back on. */
export function canSpeakWithBrowser(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/** Speak text with the browser synthesizer; resolves when finished, failed, or aborted. */
export function speakWithBrowser(text: string, signal?: AbortSignal): Promise<void> {
  if (!canSpeakWithBrowser() || !text) return Promise.resolve();
  return new Promise<void>(resolve => {
    const utterance = new SpeechSynthesisUtterance(text);
    const finish = () => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    };
    const onAbort = () => {
      window.speechSynthesis.cancel();
      finish();
    };
    utterance.onend = finish;
    utterance.onerror = finish;
    signal?.addEventListener('abort', onAbort, { once: true });
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  });
}

/* ---------------------------------------------------------------------------------------------
 * Hands-free conversation primitives. Everything below is pure (no DOM access) so it can be unit
 * tested with synthetic input: sentence splitting of streamed Markdown, the adaptive voice
 * activity detector used for end-of-speech and barge-in, the gapless playback queue, WAV
 * encoding, latency summaries, and the persisted sensitivity preference.
 * ------------------------------------------------------------------------------------------- */

/** Words that end in a period without ending the sentence (lowercase, without the period). */
const ABBREVIATIONS = new Set([
  'mr',
  'mrs',
  'ms',
  'dr',
  'st',
  'vs',
  'etc',
  'e.g',
  'i.e',
  'a.m',
  'p.m',
  'approx',
  'min',
  'mins',
  'hr',
  'hrs',
  'no',
  'jan',
  'feb',
  'mar',
  'apr',
  'jun',
  'jul',
  'aug',
  'sep',
  'sept',
  'oct',
  'nov',
  'dec',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
]);

/** Characters that may follow sentence-ending punctuation and still belong to the sentence. */
const SENTENCE_CLOSERS = '.!?…"\'”’)]*_';

/**
 * Index just past the first sentence boundary in `text`, or -1 when no complete sentence is
 * available yet. A boundary is a newline after non-blank text, or `.`, `!`, `?` or `…` (plus
 * closing quotes, brackets and emphasis marks) followed by whitespace. A period after a known
 * abbreviation or after a one- or two-digit list number at the start of a line is not a
 * boundary. Punctuation at the very end of `text` waits for the next delta, because the
 * following character decides whether it ends the sentence (`3.` vs `3.5`).
 */
export function findSentenceBoundary(text: string): number {
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '\n') {
      if (text.slice(0, i).trim()) return i + 1;
      continue;
    }
    if (!'.!?…'.includes(char)) continue;
    let end = i + 1;
    while (end < text.length && SENTENCE_CLOSERS.includes(text[end])) end++;
    if (end >= text.length) return -1;
    if (!/\s/.test(text[end])) continue;
    if (char === '.') {
      const line = text.slice(text.lastIndexOf('\n', i) + 1, i);
      const word = (line.match(/(\S+)$/)?.[1] ?? '').replace(/^[("'“‘*_]+/, '').toLowerCase();
      if (ABBREVIATIONS.has(word)) continue;
      if (/^\d{1,2}$/.test(word) && line.trim() === word) continue;
    }
    return end;
  }
  return -1;
}

/**
 * Convert one Markdown sentence or line into plain text to speak: drops headings, quote and list
 * markers, emphasis, code ticks, images and table rules; keeps link text. Returns `''` when
 * nothing speakable (no letter or digit) remains.
 */
export function speakableSentence(markdown: string): string {
  const line = markdown.trim();
  if (/^\|?[\s:|-]+\|?$/.test(line) && line.includes('-')) return '';
  const text = line
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/, '')
    .replace(/^>\s?/, '')
    .replace(/^([-*+]|\d{1,2}[.)])\s+/, '')
    .replace(/[*_`~]+/g, '')
    .replace(/\s*\|\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/^[,\s]+|[,\s]+$/g, '')
    .trim();
  return /[\p{L}\p{N}]/u.test(text) ? text : '';
}

/** Options for {@link createSentenceSplitter}. */
export interface SentenceSplitterOptions {
  /**
   * Longest sentence emitted, in characters, before it is cut at the last comma, semicolon or
   * space; keeps each synthesis call under the server's 400-character cap. Defaults to 300.
   */
  maxChars?: number;
}

/** Incremental splitter that turns streamed Markdown deltas into speakable sentences. */
export interface SentenceSplitter {
  /** Add a streamed fragment and return the speakable sentences it completed, in order. */
  push(delta: string): string[];
  /** End of the reply: return whatever is left as a final sentence (or nothing). */
  flush(): string[];
  /** Forget buffered text and fence state, e.g. when a new model round starts. */
  reset(): void;
}

/**
 * Create a {@link SentenceSplitter}. Text inside fenced code blocks is never spoken, and lines
 * that only contain Markdown syntax are dropped.
 */
export function createSentenceSplitter(options: SentenceSplitterOptions = {}): SentenceSplitter {
  const maxChars = options.maxChars ?? 300;
  let buffer = '';
  let inFence = false;

  const emit = (segment: string, out: string[]) => {
    const trimmed = segment.trim();
    if (trimmed.startsWith('```')) {
      inFence = !inFence;
      return;
    }
    if (inFence) return;
    const spoken = speakableSentence(trimmed);
    if (spoken) out.push(spoken);
  };

  const drain = (out: string[]) => {
    for (;;) {
      const boundary = findSentenceBoundary(buffer);
      if (boundary >= 0) {
        emit(buffer.slice(0, boundary), out);
        buffer = buffer.slice(boundary);
        continue;
      }
      if (buffer.length <= maxChars) return;
      const window = buffer.slice(0, maxChars);
      const cut = Math.max(window.lastIndexOf(', '), window.lastIndexOf('; '));
      const at = cut > maxChars / 3 ? cut + 1 : Math.max(window.lastIndexOf(' '), 1);
      emit(buffer.slice(0, at), out);
      buffer = buffer.slice(at);
    }
  };

  return {
    push(delta) {
      buffer += delta;
      const out: string[] = [];
      drain(out);
      return out;
    },
    flush() {
      const out: string[] = [];
      drain(out);
      emit(buffer, out);
      buffer = '';
      return out;
    },
    reset() {
      buffer = '';
      inFence = false;
    },
  };
}

/** How readily voice mode treats sound as speech. */
export type VoiceSensitivity = 'low' | 'medium' | 'high';

/** Sensitivity values in UI order. */
export const VOICE_SENSITIVITIES: VoiceSensitivity[] = ['low', 'medium', 'high'];

/** localStorage key holding the chosen {@link VoiceSensitivity}. */
export const VOICE_SENSITIVITY_KEY = 'fluxability.fluxAgent.voiceSensitivity';

/**
 * Read the saved sensitivity, defaulting to `medium`. Storage can be unavailable (private mode,
 * blocked site data), so failures fall back to the default instead of throwing.
 */
export function loadVoiceSensitivity(storage?: Pick<Storage, 'getItem'>): VoiceSensitivity {
  try {
    const value = (storage ?? window.localStorage).getItem(VOICE_SENSITIVITY_KEY);
    return VOICE_SENSITIVITIES.includes(value as VoiceSensitivity)
      ? (value as VoiceSensitivity)
      : 'medium';
  } catch {
    return 'medium';
  }
}

/** Save the sensitivity for this browser; a storage failure only means it is not remembered. */
export function saveVoiceSensitivity(
  value: VoiceSensitivity,
  storage?: Pick<Storage, 'setItem'>
): void {
  try {
    (storage ?? window.localStorage).setItem(VOICE_SENSITIVITY_KEY, value);
  } catch {
    // Not persisted; the in-memory choice still applies for this session.
  }
}

/** Detector tuning for one {@link VoiceSensitivity}. */
interface SensitivityTuning {
  /** Multiple of the learned noise floor that counts as speech. */
  factor: number;
  /** Absolute RMS below which nothing counts as speech, however quiet the room. */
  minimum: number;
}

/** Per-sensitivity detector tuning. */
const SENSITIVITY_TUNING: Record<VoiceSensitivity, SensitivityTuning> = {
  low: { factor: 4, minimum: 0.015 },
  medium: { factor: 3, minimum: 0.009 },
  high: { factor: 2.2, minimum: 0.005 },
};

/** Tuning for {@link createVoiceActivityDetector}. Durations are milliseconds. */
export interface VoiceActivityOptions {
  /** Threshold preset; defaults to `medium`. */
  sensitivity?: VoiceSensitivity;
  /** Time the level must stay above the threshold before speech starts. Defaults to 200. */
  onsetMs?: number;
  /** Speech shorter than this is discarded as a click or cough. Defaults to 300. */
  minSpeechMs?: number;
  /** Utterances end at this length regardless of pauses. Defaults to 45000. */
  maxUtteranceMs?: number;
}

/** Detector transition produced by one frame. */
export type VoiceActivityEvent = 'speech-start' | 'speech-end' | 'speech-discard' | null;

/** Adaptive voice-activity detector driven by per-frame RMS levels. */
export interface VoiceActivityDetector {
  /**
   * Feed one frame's RMS level (0-1) at `nowMs` (monotonic milliseconds) and return the
   * transition it caused, if any.
   */
  update(level: number, nowMs: number): VoiceActivityEvent;
  /** True between `speech-start` and `speech-end`/`speech-discard`. */
  readonly speaking: boolean;
  /** Current ambient RMS estimate. */
  readonly noiseFloor: number;
  /** Current speech threshold: max(floor × factor × boost, minimum × boost). */
  readonly threshold: number;
  /** Timestamp of the last frame above the threshold during speech; null otherwise. */
  readonly lastVoicedAt: number | null;
  /**
   * Multiply the threshold, e.g. 1.6 while the agent is speaking so residual echo does not count
   * as a barge-in, and set the onset time. Takes effect on the next frame.
   */
  configure(boost: number, onsetMs?: number): void;
  /** Change the sensitivity preset without losing the learned noise floor. */
  setSensitivity(sensitivity: VoiceSensitivity): void;
  /** Return to the waiting state, keeping the noise floor. */
  reset(): void;
}

/**
 * Silence that ends an utterance after `speechMs` of speech: 1300 ms for very short replies
 * ("yes", "um…") where people pause before continuing, 1000 ms under two seconds, otherwise
 * 800 ms.
 */
export function endOfSpeechSilenceMs(speechMs: number): number {
  if (speechMs < 800) return 1300;
  if (speechMs < 2000) return 1000;
  return 800;
}

/**
 * Create a {@link VoiceActivityDetector}. While not speaking it tracks the ambient level: the
 * floor follows quieter frames quickly (20 % per frame) and louder ones slowly (2 % per frame),
 * and freezes while a candidate onset is above the threshold. Speech starts after the level
 * stays above the threshold for `onsetMs`; it ends after {@link endOfSpeechSilenceMs} below
 * 75 % of the threshold (hysteresis), or at `maxUtteranceMs`. Speech shorter than `minSpeechMs`
 * is reported as `speech-discard`.
 */
export function createVoiceActivityDetector(
  options: VoiceActivityOptions = {}
): VoiceActivityDetector {
  let tuning = SENSITIVITY_TUNING[options.sensitivity ?? 'medium'];
  let onsetMs = options.onsetMs ?? 200;
  const minSpeechMs = options.minSpeechMs ?? 300;
  const maxUtteranceMs = options.maxUtteranceMs ?? 45_000;
  let boost = 1;
  let floor: number | null = null;
  let candidateSince: number | null = null;
  let speechStartedAt: number | null = null;
  let lastVoicedAt: number | null = null;
  let voicedMs = 0;
  let previousAt: number | null = null;

  const threshold = () =>
    Math.max((floor ?? tuning.minimum) * tuning.factor * boost, tuning.minimum * boost);

  const detector: VoiceActivityDetector = {
    get speaking() {
      return speechStartedAt !== null;
    },
    get noiseFloor() {
      return floor ?? 0;
    },
    get threshold() {
      return threshold();
    },
    get lastVoicedAt() {
      return lastVoicedAt;
    },
    configure(nextBoost, nextOnsetMs) {
      boost = Math.max(1, nextBoost);
      if (nextOnsetMs !== undefined) onsetMs = nextOnsetMs;
    },
    setSensitivity(sensitivity) {
      tuning = SENSITIVITY_TUNING[sensitivity];
    },
    reset() {
      candidateSince = null;
      speechStartedAt = null;
      lastVoicedAt = null;
      voicedMs = 0;
    },
    update(level, nowMs) {
      const step = previousAt === null ? 0 : Math.max(0, nowMs - previousAt);
      previousAt = nowMs;
      const limit = threshold();
      if (speechStartedAt === null) {
        if (level >= limit) {
          candidateSince ??= nowMs;
          if (nowMs - candidateSince >= onsetMs) {
            speechStartedAt = candidateSince;
            lastVoicedAt = nowMs;
            voicedMs = nowMs - candidateSince;
            candidateSince = null;
            return 'speech-start';
          }
          return null;
        }
        candidateSince = null;
        if (floor === null) floor = level;
        else floor += (level - floor) * (level < floor ? 0.2 : 0.02);
        return null;
      }
      if (level >= limit * 0.75) {
        lastVoicedAt = nowMs;
        voicedMs += step;
      }
      const silentFor = nowMs - (lastVoicedAt ?? nowMs);
      const ended =
        nowMs - speechStartedAt >= maxUtteranceMs || silentFor >= endOfSpeechSilenceMs(voicedMs);
      if (!ended) return null;
      const enough = voicedMs >= minSpeechMs;
      speechStartedAt = null;
      candidateSince = null;
      voicedMs = 0;
      return enough ? 'speech-end' : 'speech-discard';
    },
  };
  return detector;
}

/** A scheduled playback that can be stopped and reports when it ends. */
export interface PlaybackHandle {
  /** Stop now (or cancel before start); `ended` then resolves. */
  stop(): void;
  /** Resolves when playback finishes or is stopped. */
  ended: Promise<void>;
}

/** Result of {@link PlaybackSink.schedule}. */
export interface ScheduledPlayback {
  /** Clock time the item will end, or `null` when unknown (the next item then waits for `ended`). */
  endsAt: number | null;
  /** Stop handle for the scheduled item. */
  handle: PlaybackHandle;
}

/** Output used by {@link createPlaybackQueue}; the Web Audio implementation lives in the engine. */
export interface PlaybackSink<T> {
  /** Current output clock in seconds (for Web Audio, `AudioContext.currentTime`). */
  now(): number;
  /** Start `item` at clock time `at` and report when it ends and how to stop it. */
  schedule(item: T, at: number): ScheduledPlayback;
}

/** Ordered, gapless playback of items that become ready out of order. */
export interface PlaybackQueue<T> {
  /**
   * Append an item. It may still be loading: items start in enqueue order, each as soon as it
   * is ready and the previous one is scheduled. A `null` result is skipped.
   */
  enqueue(item: Promise<T | null>): void;
  /** Stop everything playing or scheduled and drop pending items. */
  stop(): void;
  /** Resolves once every item enqueued so far has played or was dropped. */
  drained(): Promise<void>;
  /** True while anything is pending, scheduled, or playing. */
  readonly busy: boolean;
}

/** Callbacks for {@link createPlaybackQueue}. */
export interface PlaybackQueueOptions {
  /** Called once per queue run with the clock time the first item starts. */
  onStart?: (at: number) => void;
  /** Called when the queue becomes idle after playing at least one item (not after `stop`). */
  onIdle?: () => void;
}

/**
 * Create a {@link PlaybackQueue}. Each ready item is scheduled at `max(now, end of previous)`,
 * so consecutive sentences abut without the gap an `ended` callback round trip would add.
 */
export function createPlaybackQueue<T>(
  sink: PlaybackSink<T>,
  options: PlaybackQueueOptions = {}
): PlaybackQueue<T> {
  let generation = 0;
  let chain: Promise<void> = Promise.resolve();
  let cursor = 0;
  let previousEnded: Promise<void> = Promise.resolve();
  let pending = 0;
  let started = false;
  const handles = new Set<PlaybackHandle>();

  const settle = (run: number) => {
    if (run !== generation) return;
    if (pending === 0 && handles.size === 0 && started) {
      started = false;
      options.onIdle?.();
    }
  };

  return {
    get busy() {
      return pending > 0 || handles.size > 0;
    },
    enqueue(item) {
      const run = generation;
      pending++;
      item.catch(() => null);
      chain = chain.then(async () => {
        let value: T | null;
        try {
          value = await item;
        } catch {
          value = null;
        }
        pending = Math.max(0, pending - 1);
        if (run !== generation) return;
        if (value === null) return settle(run);
        if (!Number.isFinite(cursor)) await previousEnded;
        if (run !== generation) return;
        const at = Math.max(sink.now(), Number.isFinite(cursor) ? cursor : 0);
        const { endsAt, handle } = sink.schedule(value, at);
        handles.add(handle);
        if (!started) {
          started = true;
          options.onStart?.(at);
        }
        cursor = endsAt ?? Number.POSITIVE_INFINITY;
        previousEnded = handle.ended;
        void handle.ended.then(() => {
          handles.delete(handle);
          if (handle.ended === previousEnded && !Number.isFinite(cursor)) cursor = 0;
          settle(run);
        });
      });
    },
    stop() {
      generation++;
      pending = 0;
      started = false;
      cursor = 0;
      previousEnded = Promise.resolve();
      chain = Promise.resolve();
      for (const handle of handles) handle.stop();
      handles.clear();
    },
    async drained() {
      const run = generation;
      await chain;
      while (run === generation && handles.size) await Promise.race([...handles].map(h => h.ended));
    },
  };
}

/**
 * Average-decimate mono samples from `fromRate` to `toRate` (Hz). Returns the input unchanged
 * when it is already at or below the target rate.
 */
export function downsample(samples: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (toRate >= fromRate) return samples;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(samples.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(samples.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j++) sum += samples[j];
    out[i] = sum / Math.max(1, end - start);
  }
  return out;
}

/** Encode mono samples in [-1, 1] as a 16-bit PCM WAV file. */
export function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const bytes = new Uint8Array(44 + samples.length * 2);
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, text: string) =>
    [...text].forEach((char, i) => view.setUint8(offset + i, char.charCodeAt(0)));
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return bytes;
}

/** Monotonic `performance.now()` marks recorded for one voice turn; absent hops were skipped. */
export interface VoiceTurnMarks {
  /** Last frame above the speech threshold: when the person actually stopped talking. */
  speechStoppedAt?: number;
  /** Detector declared end of speech (after the adaptive silence window). */
  endpointAt?: number;
  /** Transcript text returned by the server. */
  transcriptAt?: number;
  /** First streamed answer text received. */
  firstDeltaAt?: number;
  /** First sentence's audio started playing. */
  firstAudioAt?: number;
}

/** Per-hop latency of one voice turn in milliseconds; `null` when a mark is missing. */
export interface VoiceTurnTimings {
  /** Silence the detector waited before ending the utterance. */
  endpointing: number | null;
  /** End of speech (endpoint) → transcript. */
  transcribe: number | null;
  /** Transcript → first answer delta. */
  firstDelta: number | null;
  /** First delta → first sentence audio start. */
  firstAudio: number | null;
  /** Person stopped talking → first audio: the latency they perceive. */
  total: number | null;
}

/** Compute {@link VoiceTurnTimings} from marks, rounding to whole milliseconds. */
export function summarizeVoiceTurn(marks: VoiceTurnMarks): VoiceTurnTimings {
  const hop = (from?: number, to?: number) =>
    from === undefined || to === undefined ? null : Math.round(to - from);
  return {
    endpointing: hop(marks.speechStoppedAt, marks.endpointAt),
    transcribe: hop(marks.endpointAt, marks.transcriptAt),
    firstDelta: hop(marks.transcriptAt, marks.firstDeltaAt),
    firstAudio: hop(marks.firstDeltaAt, marks.firstAudioAt),
    total: hop(marks.speechStoppedAt ?? marks.endpointAt, marks.firstAudioAt),
  };
}
